import React, { useState, useEffect } from 'react';
import { MdVisibility, MdVisibilityOff, MdSchool, MdLock, MdArrowBack } from 'react-icons/md';

// Universities with SSO support — extend this list later
const UNIVERSITIES = [
  {
    id: 'uic',
    name: 'University of Illinois Chicago',
    short: 'UIC',
    ssoUrl: '/secure',
  },
];

const SSO_ERRORS = {
  no_identity:   'SSO authentication returned no user identity. Please try again.',
  create_failed: 'Could not create your account. Contact your instructor.',
  inactive:      'Your account is inactive. Contact your instructor.',
  server_error:  'A server error occurred during SSO login. Please try again.',
};

const Login = ({ onLogin, isModal = false }) => {
  // 'select' → pick SSO or password
  // 'sso'    → pick university and continue
  // 'password' → username/password form
  const [step, setStep]                 = useState('select');
  const [selectedUniv, setSelectedUniv] = useState(UNIVERSITIES[0]);
  const [username, setUsername]         = useState('');
  const [password, setPassword]         = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading]       = useState(false);
  const [error, setError]               = useState('');

  // Surface SSO errors redirected back from /secure
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ssoError = params.get('sso_error');
    if (ssoError) {
      setError(SSO_ERRORS[ssoError] || 'SSO login failed. Please try again.');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const handleSsoLogin = () => {
    window.location.href = selectedUniv.ssoUrl;
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      await onLogin({ username, password });
    } catch (err) {
      setError(err.message || 'Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const goBack = () => {
    setError('');
    setStep('select');
  };

  // ─── Header ──────────────────────────────────────────────────────────────────
  const header = !isModal && (
    <div className="bg-gradient-to-r from-blue-600 to-red-600 px-8 py-6">
      <div className="text-center">
        <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
          <span className="text-blue-600 font-bold text-2xl">AI</span>
        </div>
        <h2 className="text-2xl font-bold text-white mb-1">ERD Assistant</h2>
        <p className="text-blue-100 text-sm">University of Illinois Chicago</p>
      </div>
    </div>
  );

  // ─── Error banner ─────────────────────────────────────────────────────────────
  const errorBanner = error && (
    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
      <p className="text-red-700 text-sm">{error}</p>
    </div>
  );

  // ─── Step: select login method ────────────────────────────────────────────────
  const selectStep = (
    <div className={isModal ? 'p-6' : 'px-8 py-6'}>
      {isModal ? (
        <div className="text-center mb-6">
          <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <span className="text-blue-600 font-bold text-xl">AI</span>
          </div>
          <h3 className="text-xl font-semibold text-gray-800 mb-1">Sign in</h3>
          <p className="text-sm text-gray-500">ERD Assistant — UIC</p>
        </div>
      ) : (
        <h3 className="text-xl font-semibold text-gray-800 mb-6 text-center">
          How would you like to sign in?
        </h3>
      )}

      {errorBanner}

      <div className="space-y-3">
        {/* University SSO */}
        <button
          onClick={() => { setError(''); setStep('sso'); }}
          className="w-full flex items-center gap-4 p-4 border-2 border-blue-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-all duration-200 text-left group"
        >
          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-blue-200 transition-colors">
            <MdSchool className="text-blue-600 text-xl" />
          </div>
          <div>
            <p className="font-semibold text-gray-800 text-sm">University SSO</p>
            <p className="text-xs text-gray-500">Sign in with your university credentials</p>
          </div>
        </button>

        {/* Username / Password */}
        <button
          onClick={() => { setError(''); setStep('password'); }}
          className="w-full flex items-center gap-4 p-4 border-2 border-gray-200 rounded-xl hover:border-gray-400 hover:bg-gray-50 transition-all duration-200 text-left group"
        >
          <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-gray-200 transition-colors">
            <MdLock className="text-gray-500 text-xl" />
          </div>
          <div>
            <p className="font-semibold text-gray-800 text-sm">Username &amp; Password</p>
            <p className="text-xs text-gray-500">Sign in with your account credentials</p>
          </div>
        </button>
      </div>
    </div>
  );

  // ─── Step: SSO — pick university ─────────────────────────────────────────────
  const ssoStep = (
    <div className={isModal ? 'p-6' : 'px-8 py-6'}>
      <div className="flex items-center gap-2 mb-5">
        <button
          onClick={goBack}
          className="text-gray-400 hover:text-gray-600 transition-colors"
          title="Back"
        >
          <MdArrowBack className="text-xl" />
        </button>
        <h3 className="text-lg font-semibold text-gray-800">Select your university</h3>
      </div>

      {errorBanner}

      <div className="space-y-2 mb-6">
        {UNIVERSITIES.map((univ) => (
          <button
            key={univ.id}
            onClick={() => setSelectedUniv(univ)}
            className={`w-full flex items-center gap-3 p-3 rounded-lg border-2 transition-all duration-150 text-left ${
              selectedUniv.id === univ.id
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-blue-300'
            }`}
          >
            <div className="w-9 h-9 bg-gradient-to-br from-blue-600 to-red-600 rounded-md flex items-center justify-center flex-shrink-0">
              <span className="text-white font-bold text-xs">{univ.short}</span>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-800">{univ.name}</p>
              <p className="text-xs text-gray-500">{univ.short} NetID &amp; password</p>
            </div>
            {selectedUniv.id === univ.id && (
              <span className="ml-auto w-4 h-4 rounded-full bg-blue-500 flex-shrink-0" />
            )}
          </button>
        ))}
      </div>

      <button
        onClick={handleSsoLogin}
        className="w-full bg-gradient-to-r from-blue-600 to-red-600 hover:from-blue-700 hover:to-red-700 text-white font-medium py-3 px-4 rounded-lg transition-all duration-200 transform hover:scale-105"
      >
        Continue to {selectedUniv.short} Login
      </button>

      <p className="mt-3 text-center text-xs text-gray-400">
        You will be redirected to your university's login page.
      </p>
    </div>
  );

  // ─── Step: username / password ────────────────────────────────────────────────
  const passwordStep = (
    <div className={isModal ? 'p-6' : 'px-8 py-6'}>
      <div className="flex items-center gap-2 mb-5">
        <button
          onClick={goBack}
          className="text-gray-400 hover:text-gray-600 transition-colors"
          title="Back"
        >
          <MdArrowBack className="text-xl" />
        </button>
        <h3 className="text-lg font-semibold text-gray-800">Sign in with password</h3>
      </div>

      {errorBanner}

      <form onSubmit={handlePasswordSubmit} className="space-y-4">
        <div>
          <label className="block text-gray-700 text-sm font-medium mb-2">
            Username
          </label>
          <input
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
            placeholder="Enter your username"
            required
            disabled={isLoading}
            autoFocus
          />
        </div>

        <div>
          <label className="block text-gray-700 text-sm font-medium mb-2">
            Password
          </label>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
              placeholder="Enter your password"
              required
              disabled={isLoading}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors focus:outline-none"
              disabled={isLoading}
            >
              {showPassword
                ? <MdVisibilityOff className="h-5 w-5" />
                : <MdVisibility className="h-5 w-5" />}
            </button>
          </div>
        </div>

        <button
          type="submit"
          disabled={isLoading || !username || !password}
          className="w-full bg-gradient-to-r from-blue-600 to-red-600 hover:from-blue-700 hover:to-red-700 disabled:from-gray-400 disabled:to-gray-500 text-white font-medium py-3 px-4 rounded-lg transition-all duration-200 transform hover:scale-105 disabled:scale-100 disabled:cursor-not-allowed"
        >
          {isLoading ? (
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
              Signing in…
            </div>
          ) : (
            'Sign In'
          )}
        </button>
      </form>

      <div className="mt-5 pt-4 border-t border-gray-200 text-center text-sm text-gray-500">
        <p className="mb-1">Need help accessing your account?</p>
        <p className="text-xs">Contact your instructor for assistance</p>
      </div>
    </div>
  );

  // ─── Compose ──────────────────────────────────────────────────────────────────
  const body =
    step === 'select'   ? selectStep :
    step === 'sso'      ? ssoStep :
                          passwordStep;

  const content = (
    <>
      {header}
      {body}
      {!isModal && (
        <div className="bg-gray-50 px-8 py-3 flex justify-center">
          <span className="text-xs text-gray-400">AI ERD Assistant v1.0</span>
        </div>
      )}
    </>
  );

  if (isModal) return content;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-red-600 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
        {content}
      </div>
    </div>
  );
};

export default Login;
